// Découpe du template client par domaine métier.
//
// L'éditorial est le SOCLE : tout site en a besoin, et le retirer ne laisserait
// rien de fonctionnel. Commerce et auth sont des options indépendantes — un
// catalogue sans compte client existe, un espace membre sans catalogue aussi.
//
// Deux mécanismes, parce que deux natures de contenu :
//   • des fichiers ENTIERS, propres à un domaine → simplement pas écrits ;
//   • des COUTURES dans des fichiers partagés (platform.ts, __root.tsx) →
//     délimitées par des marqueurs que le scaffold retire.
// Le template reste donc exécutable tel quel : sans drapeau, il rend la
// variante complète, et c'est ce que la sandbox de dogfood construit.

/** Domaines optionnels. L'éditorial n'en est pas un : c'est le socle. */
export const OPTIONAL_FEATURES = ["commerce", "auth"] as const

export type OptionalFeature = (typeof OPTIONAL_FEATURES)[number]

/**
 * Fichiers et dossiers propres à un domaine, en chemins relatifs à la racine
 * du template. Un préfixe couvre son sous-arbre.
 */
const FEATURE_PATHS: Record<OptionalFeature, string[]> = {
	commerce: [
		"apps/webapp/src/commerce",
		"apps/webapp/src/cart.ts",
		"apps/webapp/src/server/cart.ts",
		"apps/webapp/src/server/cartMiddleware.ts",
	],
	auth: [
		"apps/webapp/src/authOps.ts",
		"apps/webapp/src/server/auth.ts",
		"apps/webapp/src/server/authMiddleware.ts",
		"apps/webapp/src/server/session.ts",
	],
}

/** Deps `@wp-reactor/*` que le domaine est seul à tirer. */
const FEATURE_DEPS: Record<OptionalFeature, string[]> = {
	commerce: ["@wp-reactor/commerce"],
	auth: ["@wp-reactor/auth"],
}

/** Vrai si ce chemin de template n'a de sens que pour un domaine désactivé. */
export function isDisabledPath(
	relativePath: string,
	enabled: ReadonlySet<string>,
): boolean {
	const normalized = relativePath.split("\\").join("/")
	for (const feature of OPTIONAL_FEATURES) {
		if (enabled.has(feature)) continue
		for (const path of FEATURE_PATHS[feature]) {
			if (normalized === path || normalized.startsWith(`${path}/`)) return true
		}
	}
	return false
}

// Trois syntaxes de commentaire, parce que trois natures de fichier : `//` en
// TS, `#` en .env et YAML, `{/* */}` en JSX — où un `//` serait rendu comme du
// texte au lieu d'être un commentaire.
// `(?!end\b)` : sans lui, `@wpr:end` passe pour l'ouverture d'un domaine
// nommé « end » et tout bloc se lit comme imbriqué.
const OPEN =
	/^\s*(?:\/\/|#|\{\/\*)\s*@wpr:(?!end\b)([a-z]+)(:wrap)?\s*(?:\*\/\})?\s*$/
const CLOSE = /^\s*(?:\/\/|#|\{\/\*)\s*@wpr:end\s*(?:\*\/\})?\s*$/

/**
 * Retire les marqueurs `@wpr:<domaine>` et, si le domaine est désactivé, ce
 * qu'ils entourent.
 *
 * Deux formes, parce que retirer une enveloppe JSX n'est pas retirer un bloc :
 *
 *   // @wpr:commerce          → bloc : disparaît en entier
 *   …
 *   // @wpr:end
 *
 *   // @wpr:auth:wrap         → enveloppe : seules la première et la dernière
 *   <AuthProvider …>            ligne disparaissent, le reste remonte d'un cran
 *   {children}                  d'indentation pour rester lisible.
 *   </AuthProvider>
 *   // @wpr:end
 *
 * Les marqueurs ne s'imbriquent pas : un `@wpr:` avant le `@wpr:end` du
 * précédent est une erreur, pas une imbrication silencieusement acceptée.
 */
export function applyFeatureMarkers(
	content: string,
	enabled: ReadonlySet<string>,
): string {
	if (!content.includes("@wpr:")) return content

	const lines = content.split("\n")
	const out: string[] = []
	let open: { feature: string; wrap: boolean; body: string[] } | null = null

	for (const line of lines) {
		const opening = OPEN.exec(line)
		if (opening) {
			if (open) {
				throw new Error(
					`Marqueur @wpr:${opening[1]} ouvert alors que @wpr:${open.feature} ne l'est pas encore fermé.`,
				)
			}
			open = { feature: opening[1], wrap: Boolean(opening[2]), body: [] }
			continue
		}

		if (CLOSE.test(line)) {
			if (!open) throw new Error("@wpr:end sans marqueur ouvrant.")
			out.push(...resolveBlock(open, enabled))
			open = null
			continue
		}

		if (open) open.body.push(line)
		else out.push(line)
	}

	if (open) {
		throw new Error(`Marqueur @wpr:${open.feature} jamais fermé.`)
	}
	return out.join("\n")
}

function resolveBlock(
	block: { feature: string; wrap: boolean; body: string[] },
	enabled: ReadonlySet<string>,
): string[] {
	if (enabled.has(block.feature)) return block.body
	if (!block.wrap) return []
	// Enveloppe retirée : on laisse tomber la balise ouvrante et la fermante,
	// puis on remonte le contenu d'un niveau — le template indente aux tabs.
	return block.body
		.slice(1, -1)
		.map((line) => (line.startsWith("\t") ? line.slice(1) : line))
}

/**
 * Retire d'un package.json les deps qu'aucun domaine actif ne tire.
 *
 * Les marqueurs ne peuvent rien ici : JSON n'a pas de commentaires. La
 * suppression est donc ciblée, ligne à ligne, sur les seules deps recensées.
 */
export function stripFeatureDeps(
	content: string,
	enabled: ReadonlySet<string>,
): string {
	let out = content
	for (const feature of OPTIONAL_FEATURES) {
		if (enabled.has(feature)) continue
		for (const dep of FEATURE_DEPS[feature]) {
			out = out.replace(
				new RegExp(
					`^\\s*"${dep.replace("/", "\\/")}"\\s*:\\s*"[^"]*",?\\n`,
					"m",
				),
				"",
			)
		}
	}
	return out
}
